import { Currency, FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useSWRMutation from 'swr/mutation';
import { usePayeeCheck } from '../../../api/use-payee-check';
import { useData } from '../../../contexts/data-context/use-data';
import { PayrollRecordView } from '../../../types/entity-types';
import { deleteSwrFetcherWithDynamicUrl } from '../../../utils/delete-swr-fetcher-with-dynamic-url';
import { postSwrFetcherWithDynamicUrl } from '../../../utils/post-swr-fetcher-with-dynamic-url';
import { putSwrFetcherWithDynamicUrl } from '../../../utils/put-swr-fetcher-with-dynamic-url';
import { getBeneficiaryName } from '../../payments/utils/get-beneficiary-name';
import { isVopCheckRequired } from '../../payments/utils/is-vop-check-required';
import { PayrollFormData } from '../components/financial-information-step/financial-information-form';

export interface PayrollRecordInput {
  employeeId: string;
}

export type PayrollRecordUpdateInput = {
  payrollId: string;
  payrollRecordId: string;
  netPay: number;
};

type SourceAccountConfig =
  | {
      isPresent: false;
    }
  | {
      isPresent: true;
      id: string;
      currency: Currency;
      fspAdapter: FinancialServiceProviderAdapters;
    };

const PAYROLL_API_PATH = '/api/payrolls';

export const usePayrollRecord = () => {
  const { isVopPayrollLive } = useFlags<{ isVopPayrollLive: boolean }>();

  const { mutations } = useData();
  const {
    mutations: { triggerVerifyPayee },
  } = usePayeeCheck();

  const { trigger, isMutating, error } = useSWRMutation<
    PayrollRecordView,
    AxiosError,
    string,
    { urlSuffix: string } & PayrollRecordInput
  >(PAYROLL_API_PATH, postSwrFetcherWithDynamicUrl);

  const { trigger: triggerUpdateRecord } = useSWRMutation<
    PayrollRecordView,
    AxiosError,
    string,
    { urlSuffix: string } & PayrollRecordUpdateInput
  >(PAYROLL_API_PATH, putSwrFetcherWithDynamicUrl);

  const { trigger: triggerDeleteRecord } = useSWRMutation<void, AxiosError, string, { urlSuffix: string }>(
    PAYROLL_API_PATH,
    deleteSwrFetcherWithDynamicUrl,
  );

  const addPayrollRecords = async (payrollId: string, records: PayrollRecordInput[]): Promise<PayrollRecordView[]> => {
    try {
      const results = await Promise.all(
        records.map((record) =>
          trigger({
            urlSuffix: `/${payrollId}/records`,
            ...record,
          }),
        ),
      );
      await mutations.handleIncrementalUpdates();
      return results;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          payrollId,
          records,
          recordsCount: records.length,
          employeeIds: records.map((r) => r.employeeId),
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined,
        },
        tags: {
          component: 'usePayrollRecord',
          action: 'addPayrollRecords',
        },
      });
      throw error;
    }
  };

  const updatePayrollRecords = async (
    payrollId: string,
    payrollRecords: PayrollRecordView[],
    employees: PayrollFormData['employees'],
    sourceAccountConfig: SourceAccountConfig,
  ) => {
    const updateRecord = updatePayrollRecord(sourceAccountConfig);

    try {
      const updatePromises = payrollRecords.map((payrollRecord) => {
        const employee = employees.find((e) => e.id === payrollRecord.employeeId);
        if (!employee) {
          const warningMessage = `No employee found for payroll record with employeeId: ${payrollRecord.employeeId}`;
          console.warn(warningMessage);

          Sentry.captureMessage(warningMessage, {
            level: 'warning',
            extra: {
              payrollId,
              payrollRecordId: payrollRecord.id,
              employeeId: payrollRecord.employeeId,
              availableEmployeeIds: employees.map((e) => e.id),
            },
            tags: {
              component: 'usePayrollRecord',
              action: 'updatePayrollRecords',
              issue: 'employee_not_found',
            },
          });

          return Promise.resolve();
        }

        const fullName = getBeneficiaryName({
          firstName: employee.firstName,
          lastName: employee.lastName,
        });

        return updateRecord(payrollId, payrollRecord.id, Number(employee.netPay), {
          id: employee.id,
          name: fullName,
          iban: employee.destination?.iban,
        });
      });

      await Promise.all(updatePromises);

      await mutations.handleIncrementalUpdates();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          payrollId,
          payrollRecordsCount: payrollRecords.length,
          employeesCount: employees.length,
          payrollRecordIds: payrollRecords.map((r) => r.id),
          employeeIds: employees.map((e) => e.id),
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined,
        },
        tags: {
          component: 'usePayrollRecord',
          action: 'updatePayrollRecords',
        },
      });
      throw error;
    }
  };

  const updatePayrollRecord = (sourceAccountConfig: SourceAccountConfig) => {
    return async (
      payrollId: string,
      payrollRecordId: string,
      netPay: number,
      employeeInfo: { id: string; name: string; iban?: string | null },
    ) => {
      try {
        const updatedRecord = await triggerUpdateRecord({
          urlSuffix: `/${payrollId}/records/${payrollRecordId}`,
          payrollId,
          payrollRecordId,
          netPay,
        });

        if (isVopPayrollLive && sourceAccountConfig.isPresent) {
          const shouldTriggerVopCheck = isVopCheckRequired({
            currency: sourceAccountConfig.currency,
            fspAdapter: sourceAccountConfig.fspAdapter,
          });

          if (shouldTriggerVopCheck) {
            await triggerVerifyPayee({
              paymentAccountId: sourceAccountConfig.id,
              employeeId: employeeInfo.id,
              name: employeeInfo.name,
              iban: employeeInfo.iban,
            });
          }
        }

        await mutations.handleIncrementalUpdates();
        return updatedRecord;
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            payrollId,
            payrollRecordId,
            netPay,
            netPayType: typeof netPay,
            urlSuffix: `/${payrollId}/records/${payrollRecordId}`,
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
            errorStack: error instanceof Error ? error.stack : undefined,
          },
          tags: {
            component: 'usePayrollRecord',
            action: 'updatePayrollRecord',
          },
        });
        throw error;
      }
    };
  };

  const deletePayrollRecords = async (payrollId: string, payrollRecordIds: string[]) => {
    try {
      await Promise.all(
        payrollRecordIds.map((recordId) =>
          triggerDeleteRecord({
            urlSuffix: `/${payrollId}/records/${recordId}`,
          }),
        ),
      );

      // Use handleDeleteUpdates to properly update the cache for deleted payroll records
      await mutations.handleDeleteUpdates({
        entityType: 'payrollRecord',
        entityIds: payrollRecordIds,
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          payrollId,
          payrollRecordIds,
          recordsCount: payrollRecordIds.length,
        },
      });
      throw error;
    }
  };

  return {
    addPayrollRecords,
    updatePayrollRecord,
    updatePayrollRecords,
    deletePayrollRecords,
    isLoading: isMutating,
    error,
  };
};
